import React from 'react';

import PropTypes from 'prop-types';
import { useQueryParams } from 'use-query-params';

import { isFilledQueryValue, omitEmptyQueryValues, wrapFilterSetQuery } from 'utils/queryValues';

import { Mui } from 'components';
import InputSearch from 'components/common/InputSearch/Loadable';

// Local components
import CollapsibleFilters from './CollapsibleFilters';

const FULL_WIDTH_DISPLAY_NAME = 'FiltersFullWidth';
const TOOLBAR_FIELD_WIDTH = 500;
const IGNORED_ACTIVE_KEYS = ['page', 'page_size', 'ordering', 'search'];

const useStyles = Mui.makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    width: '100%',
  },
  toolbarField: {
    width: TOOLBAR_FIELD_WIDTH,
    maxWidth: '100%',
  },
  fullHeightContainer: {
    height: `calc(100% + ${theme.spacing(2)}px)`,
  },
  fullHeightItem: {
    height: '100%',
  },
}));

const isFilledCycle = value =>
  Array.isArray(value)
    ? value.some(isFilledCycle)
    : isFilledQueryValue(value) && value !== false && String(value) !== 'all';

export const hasActiveValues = (values, { ignoreKeys = IGNORED_ACTIVE_KEYS } = {}) => {
  if (!values || typeof values !== 'object') return false;

  const ignored = new Set(ignoreKeys);

  return Object.entries(values).some(([key, value]) => {
    if (ignored.has(key)) return false;
    if (key === 'cycle') return isFilledCycle(value);
    if (value === false) return false;
    return isFilledQueryValue(value);
  });
};

export const mapCommaToSelected = (value, key = 'key') =>
  String(value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
    .map(item => ({ [key]: item }));

export function useFilterQueryParams(paramConfig, options) {
  const [query, setQueryRaw] = useQueryParams(paramConfig);
  return [query, wrapFilterSetQuery(setQueryRaw, options)];
}

const patchQuery = (setQuery, patch, path) => {
  if (!setQuery) return;
  const nextPatch = omitEmptyQueryValues(patch);
  setQuery(prevQuery => ({
    ...prevQuery,
    page: 1,
    ...(path
      ? { [path]: omitEmptyQueryValues({ ...prevQuery?.[path], ...nextPatch }) }
      : nextPatch),
  }));
};

function FullWidth({ children }) {
  return children;
}

FullWidth.displayName = FULL_WIDTH_DISPLAY_NAME;

FullWidth.propTypes = {
  children: PropTypes.node,
};

const isFullWidthChild = child =>
  child?.type === FullWidth || child?.type?.displayName === FULL_WIDTH_DISPLAY_NAME;

const renderGridItem = (child, index) => {
  if (!child) return null;

  const key = child.key ?? `filter-${index}`;

  return isFullWidthChild(child) ? (
    <Mui.Grid item xs={12} key={key}>
      {child.props.children}
    </Mui.Grid>
  ) : (
    <Mui.Grid item xs={12} sm={6} md={4} key={key}>
      {child}
    </Mui.Grid>
  );
};

function GridItems({ children }) {
  return (
    <Mui.Grid container spacing={2} alignItems="flex-start">
      {React.Children.map(children, renderGridItem)}
    </Mui.Grid>
  );
}

GridItems.displayName = 'FiltersGridItems';

GridItems.propTypes = {
  children: PropTypes.node,
};

function SearchField({ config, value, onPatch }) {
  return (
    <InputSearch
      className={config.className}
      count={config.count}
      defaultValue={config.defaultValue ?? value}
      onSearch={({ search }) =>
        config.onSearch ? config.onSearch({ search }) : onPatch({ search })
      }
      forceShowCount={config.forceShowCount !== false}
      disabled={config.disabled}
      variant="outlined"
      fullWidth
    />
  );
}

SearchField.propTypes = {
  config: PropTypes.object.isRequired,
  value: PropTypes.string,
  onPatch: PropTypes.func.isRequired,
};

function OrderingField({ config, value, onPatch }) {
  return (
    <Mui.TextField
      label="Ordenação"
      name="sort"
      onChange={event => {
        const ordering = event.target.value;
        if (config.onChange) config.onChange(ordering);
        else onPatch({ ordering });
      }}
      select
      SelectProps={{ native: true }}
      value={config.value ?? value}
      disabled={config.disabled}
      variant="outlined"
      margin="none"
      fullWidth>
      {(config.options || []).map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </Mui.TextField>
  );
}

OrderingField.propTypes = {
  config: PropTypes.object.isRequired,
  value: PropTypes.string,
  onPatch: PropTypes.func.isRequired,
};

function Toolbar({ search, ordering, values, onPatch }) {
  const classes = useStyles();

  return (
    <Mui.Box
      className={classes.toolbar}
      justifyContent={ordering && !search ? 'flex-end' : 'space-between'}>
      {!!search && (
        <Mui.Box className={classes.toolbarField}>
          <SearchField config={search} value={values.search ?? ''} onPatch={onPatch} />
        </Mui.Box>
      )}
      {!!ordering && (
        <Mui.Box className={classes.toolbarField}>
          <OrderingField config={ordering} value={values.ordering ?? ''} onPatch={onPatch} />
        </Mui.Box>
      )}
    </Mui.Box>
  );
}

Toolbar.propTypes = {
  search: PropTypes.object,
  ordering: PropTypes.object,
  values: PropTypes.object.isRequired,
  onPatch: PropTypes.func.isRequired,
};

const resolveActiveValues = (clearValues, values, query) =>
  clearValues &&
  Object.fromEntries(Object.keys(clearValues).map(key => [key, values[key] ?? query?.[key]]));

function Filters({
  query,
  setQuery,
  queryPath,
  search,
  ordering,
  clearValues,
  active,
  onClear,
  disableGrid = false,
  defaultExpanded,
  footer,
  fullHeight = false,
  title,
  clearLabel,
  children,
}) {
  const classes = useStyles();
  const searchConfig = search === true ? {} : search;
  const orderingConfig = Array.isArray(ordering) ? { options: ordering } : ordering;
  const values = (queryPath ? query?.[queryPath] : query) || {};
  const hasPanel = React.Children.toArray(children).some(Boolean);
  const canClear = Boolean(onClear || clearValues);
  const isActive =
    active ?? hasActiveValues(resolveActiveValues(clearValues, values, query), { ignoreKeys: [] });

  const handlePatch = patch => patchQuery(setQuery, patch, queryPath);
  const handleClear = () => (onClear ? onClear() : handlePatch(clearValues));

  return (
    <Mui.Grid
      container
      spacing={2}
      className={fullHeight ? classes.fullHeightContainer : undefined}>
      {!!(searchConfig || orderingConfig) && (
        <Mui.Grid item xs={12}>
          <Toolbar
            search={searchConfig}
            ordering={orderingConfig}
            values={values}
            onPatch={handlePatch}
          />
        </Mui.Grid>
      )}
      {hasPanel && (
        <Mui.Grid item xs={12} className={fullHeight ? classes.fullHeightItem : undefined}>
          <CollapsibleFilters
            active={isActive}
            onClear={isActive && canClear ? handleClear : undefined}
            title={title}
            clearLabel={clearLabel}
            defaultExpanded={defaultExpanded}
            footer={footer}
            fullHeight={fullHeight}>
            {disableGrid ? children : <GridItems>{children}</GridItems>}
          </CollapsibleFilters>
        </Mui.Grid>
      )}
    </Mui.Grid>
  );
}

Filters.FullWidth = FullWidth;
Filters.Grid = GridItems;

Filters.propTypes = {
  query: PropTypes.object,
  setQuery: PropTypes.func,
  queryPath: PropTypes.string,
  search: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      className: PropTypes.string,
      count: PropTypes.any,
      defaultValue: PropTypes.string,
      disabled: PropTypes.bool,
      forceShowCount: PropTypes.bool,
      onSearch: PropTypes.func,
    }),
  ]),
  ordering: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
    PropTypes.shape({
      disabled: PropTypes.bool,
      onChange: PropTypes.func,
      options: PropTypes.arrayOf(
        PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }),
      ),
      value: PropTypes.string,
    }),
  ]),
  clearValues: PropTypes.object,
  active: PropTypes.bool,
  onClear: PropTypes.func,
  disableGrid: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
  footer: PropTypes.node,
  fullHeight: PropTypes.bool,
  title: PropTypes.string,
  clearLabel: PropTypes.string,
  children: PropTypes.node,
};

export default Filters;
